import { useState } from "react";
import {
  saveDailyExpenseDraft,
  submitDailyExpense,
  submitDailyExpenseNoExpense,
} from "../../services/dailyExpense";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { DailyExpenseDay, DailyExpenseLineInput } from "../../types/daily-expense.type";
import { formatNumber } from "../../utils";
import { DayWrite } from "./DayFormFooter";
import { formatBusinessDate } from "./format";

/**
 * The branch's three writes on one day. Every answer is the day as it now stands, handed to
 * `onDay` so the form shows what the server holds. A refusal (closed month, a document head office
 * already took, a zero day sent the plain way) arrives as the server's Thai message in the global
 * toast, and the form keeps what the user typed.
 */
export function useDailyExpenseWrites(
  businessDate: string,
  onDay: (day: DailyExpenseDay) => void,
) {
  const [pendingWrite, setPendingWrite] = useState<DayWrite | null>(null);
  const dateText = formatBusinessDate(businessDate);

  const saveDraft = async (lines: DailyExpenseLineInput[]) => {
    setPendingWrite("DRAFT");
    try {
      const { data } = await saveDailyExpenseDraft(businessDate, { lines });
      if (data.data) onDay(data.data);
      pushGlobalSuccess(`บันทึกร่างแล้ว · ค่าใช้จ่ายวันที่ ${dateText} ยังไม่ส่งให้ HO`);
    } catch {
      // The interceptor has already shown the server's message; the typed values stay.
    } finally {
      setPendingWrite(null);
    }
  };

  /** ส่งให้ HO submits what was saved last, so the form is saved first. */
  const submit = async (lines: DailyExpenseLineInput[]) => {
    setPendingWrite("SUBMIT");
    try {
      const saved = await saveDailyExpenseDraft(businessDate, { lines });
      if (saved.data.data) onDay(saved.data.data);
      const { data } = await submitDailyExpense(businessDate);
      if (data.data) onDay(data.data);
      pushGlobalSuccess(
        `ส่งให้ HO แล้ว · ค่าใช้จ่ายวันที่ ${dateText} รวม ${formatNumber(data.data?.totals.total ?? 0)} บาท`,
      );
    } catch {
      // The interceptor has already shown the server's message. A refused submit leaves the
      // draft the first call saved, which is what the form now shows.
    } finally {
      setPendingWrite(null);
    }
  };

  const declareNoExpense = async () => {
    setPendingWrite("NO_EXPENSE");
    try {
      const { data } = await submitDailyExpenseNoExpense(businessDate);
      if (data.data) onDay(data.data);
      pushGlobalSuccess(`ส่งให้ HO แล้ว · วันที่ ${dateText} ไม่มีค่าใช้จ่าย`);
    } catch {
      // The interceptor has already shown the server's message.
    } finally {
      setPendingWrite(null);
    }
  };

  return { pendingWrite, saveDraft, submit, declareNoExpense };
}
